import { Code2, Workflow, Megaphone, ArrowUpRight, Check } from "lucide-react";

const services = [
  {
    icon: Code2,
    tag: "01",
    title: "Website Development",
    desc: "Fast, modern, conversion-focused websites built with the same engineering standards as the world's top product teams.",
    points: ["Lightning-fast Core Web Vitals", "Headless CMS & Next.js stacks", "Conversion-tuned UX"],
  },
  {
    icon: Workflow,
    tag: "02",
    title: "Business Systems",
    desc: "Automation, CRM, and internal tools that remove friction from your operations and free your team to do their best work.",
    points: ["End-to-end workflow automation", "Custom CRM & dashboards", "API integrations & ops tooling"],
  },
  {
    icon: Megaphone,
    tag: "03",
    title: "Marketing Engines",
    desc: "SEO, paid ads, and high-converting funnels — measured, optimised, and scaled until growth becomes predictable.",
    points: ["Technical & content SEO", "Paid acquisition (Google / Meta)", "Funnels, CRO & analytics"],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-16 md:py-24 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#00F0FF]">
              / Services
            </div>
            <h2 className="mt-4 font-display text-5xl md:text-6xl font-extrabold tracking-[-0.03em] leading-[0.95]">
              Three disciplines.
              <br />
              <span className="text-slate-500">One growth engine.</span>
            </h2>
          </div>
          <p className="md:max-w-sm text-slate-400 text-base md:text-lg leading-relaxed">
            We unify product, operations and marketing — so every line of code
            you ship compounds revenue, not complexity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((s) => (
            <article
              key={s.title}
              data-testid={`service-card-${s.tag}`}
              className="reveal group relative bg-[#0A0F1C] border border-white/[0.08] rounded-3xl p-8 md:p-10 hover:border-[#007AFF]/50 transition-colors duration-500 overflow-hidden"
            >
              {/* hover glow */}
              <div className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "radial-gradient(420px circle at var(--x,50%) var(--y,0%), rgba(0,122,255,0.18), transparent 60%)",
                }}
              />
              <div className="flex items-center justify-between mb-12">
                <span className="font-mono-clean text-xs text-slate-500">
                  {s.tag}
                </span>
                <span className="grid place-items-center h-11 w-11 rounded-2xl bg-white/5 border border-white/10 group-hover:bg-[#007AFF]/15 group-hover:border-[#007AFF]/40 transition-colors">
                  <s.icon className="h-5 w-5 text-[#00F0FF]" />
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                {s.title}
              </h3>
              <p className="mt-4 text-slate-400 leading-relaxed">{s.desc}</p>

              <ul className="mt-8 space-y-2.5">
                {s.points.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="h-4 w-4 mt-0.5 text-[#00F0FF] shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid={`service-link-${s.tag}`}
                className="mt-10 inline-flex items-center gap-1.5 text-sm font-semibold text-white"
              >
                Start a project
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
